import { useState, useCallback } from 'react';
import { useToast, getErrorMessage, useServices } from '@/app/providers';
import type { FileVisibility, PublicLinkResponse, UploadedFile } from '@yawi/types';

interface UseFileSharingReturn {
  isUpdating: boolean;
  error: string | null;
  updateVisibility: (id: string, visibility: FileVisibility) => Promise<UploadedFile | null>;
  shareWithUsers: (id: string, userIds: string[]) => Promise<UploadedFile | null>;
  unshareWithUser: (id: string, userId: string) => Promise<UploadedFile | null>;
  shareWithRoles: (id: string, roles: string[]) => Promise<UploadedFile | null>;
  generatePublicLink: (id: string) => Promise<PublicLinkResponse | null>;
  revokePublicLink: (id: string) => Promise<UploadedFile | null>;
}

/**
 * Hook para gestionar el compartir archivos
 *
 * @example
 * const { updateVisibility, shareWithUsers, isUpdating } = useFileSharing();
 *
 * const handleShare = async () => {
 *   await shareWithUsers(fileId, selectedUserIds);
 * };
 */
export function useFileSharing(): UseFileSharingReturn {
  const toast = useToast();
  const { fileRepo } = useServices();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateVisibility = useCallback(
    async (id: string, visibility: FileVisibility): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.updateVisibility(id, visibility);
        toast.success('Visibilidad actualizada');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al actualizar visibilidad');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  const shareWithUsers = useCallback(
    async (id: string, userIds: string[]): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.shareWithUsers(id, userIds);
        toast.success('Archivo compartido con usuarios');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al compartir');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  const unshareWithUser = useCallback(
    async (id: string, userId: string): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.unshareWithUser(id, userId);
        toast.success('Acceso removido');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al remover acceso');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  const shareWithRoles = useCallback(
    async (id: string, roles: string[]): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.shareWithRoles(id, roles);
        toast.success('Archivo compartido con roles');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al compartir');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  const generatePublicLink = useCallback(
    async (id: string): Promise<PublicLinkResponse | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.generatePublicLink(id);
        toast.success('Link público generado');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al generar link');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  const revokePublicLink = useCallback(
    async (id: string): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setError(null);
      try {
        const result = await fileRepo.revokePublicLink(id);
        toast.success('Link público revocado');
        return result;
      } catch (err) {
        const errorMsg = getErrorMessage(err);
        setError(errorMsg);
        toast.error(errorMsg, 'Error al revocar link');
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [fileRepo, toast]
  );

  return {
    isUpdating,
    error,
    updateVisibility,
    shareWithUsers,
    unshareWithUser,
    shareWithRoles,
    generatePublicLink,
    revokePublicLink,
  };
}
